import { useCallback, useRef, useState } from 'react';

import { OFFICE_USE_BLOCK, isOfficeUseBlock } from 'components/Teleconsent/forms/officeUseBlock';
import { ConsentSection, ContentBlock } from 'components/Teleconsent/types';
import { UpdateCustomConsentFormPayload } from 'types/teleconsent';

export type FormBuilderSettings = {
  title: string;
  subtitle: string;
  intro: string;
  pdfTitle: string;
  includeOfficeUseTable: boolean;
  isEnabled: boolean;
  headerSubtitle: string;
};

export type BuilderBlock = ContentBlock & { _id: string };

export type BuilderSection = {
  _id: string;
  navLabel: string;
  navLabelOverride: string;
  heading: string;
  sectionTitle: string;
  hideSectionTitle: boolean;
  blocks: BuilderBlock[];
};

function makeId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function stripOfficeUseBlocks(blocks: BuilderBlock[]): BuilderBlock[] {
  return blocks.filter((b) => !isOfficeUseBlock(b));
}

function toConsentSection(s: BuilderSection, i: number): ConsentSection {
  return {
    id: s._id,
    navLabel: s.navLabelOverride.trim() || `Section ${i + 1}`,
    heading: s.heading,
    sectionTitle: s.sectionTitle || undefined,
    hideSectionTitle: s.hideSectionTitle || undefined,
    blocks: stripOfficeUseBlocks(s.blocks),
  };
}

export type UseFormBuilderStateReturn = {
  settings: FormBuilderSettings;
  sections: BuilderSection[];
  canUndo: boolean;
  canRedo: boolean;
  undo: () => void;
  redo: () => void;
  updateSettings: (patch: Partial<FormBuilderSettings>) => void;
  hydrateSections: (sections: ConsentSection[]) => void;
  addSection: () => void;
  removeSection: (sectionId: string) => void;
  updateSection: (
    sectionId: string,
    patch: Partial<Pick<BuilderSection, 'navLabelOverride' | 'heading' | 'sectionTitle' | 'hideSectionTitle'>>,
  ) => void;
  reorderSections: (fromIndex: number, toIndex: number) => void;
  addBlock: (sectionId: string, blockType: string, atIndex?: number) => void;
  duplicateBlock: (sectionId: string, blockId: string) => void;
  removeBlock: (sectionId: string, blockId: string) => void;
  updateBlock: (sectionId: string, blockId: string, patch: Partial<ContentBlock>) => void;
  reorderBlocks: (sectionId: string, fromIndex: number, toIndex: number) => void;
  moveBlock: (fromSectionId: string, blockId: string, toSectionId: string, toIndex: number) => void;
  serialize: () => UpdateCustomConsentFormPayload;
};

const MAX_HISTORY = 50;

function makeDefaultBlock(blockType: string): ContentBlock {
  switch (blockType) {
    case 'intro-text':
      return { type: 'paragraph', text: 'Introduction text…' };
    case 'paragraph':
      return { type: 'paragraph', text: 'Paragraph text…' };
    case 'bullet-list':
      return { type: 'bullet-list', items: ['Item 1', 'Item 2'] };
    case 'numbered-list':
      return { type: 'bullet-list', items: ['Item 1', 'Item 2'], listStyle: 'numbered' as const };
    case 'lettered-list':
      return { type: 'bullet-list', items: ['Item 1', 'Item 2'], listStyle: 'lettered' as const };
    case 'signature':
      return { type: 'signature', required: true };
    case 'initials-pad':
      return {
        type: 'initials-pad',
        required: true,
        rows: [
          { id: makeId(), text: 'I acknowledge that telehealth services may be provided remotely.' },
          { id: makeId(), text: 'I understand that I may withdraw consent at any time.' },
          { id: makeId(), text: 'I confirm that the information I have provided is accurate.' },
        ],
      };
    case 'checklist':
      return {
        type: 'checklist',
        id: makeId(),
        label: 'Checkbox',
        options: [
          { id: makeId(), label: 'Option 1' },
          { id: makeId(), label: 'Option 2' },
        ],
      };
    case 'field-firstname':
      return {
        type: 'field-group',
        halfWidth: true,
        fields: [{ type: 'text', id: 'firstName', label: 'First Name', required: true }],
      };
    case 'field-lastname':
      return {
        type: 'field-group',
        halfWidth: true,
        fields: [{ type: 'text', id: 'lastName', label: 'Last Name', required: true }],
      };
    case 'field-initials':
      return {
        type: 'field-group',
        halfWidth: true,
        fields: [{ type: 'text', id: 'initials', label: 'Initials' }],
      };
    case 'field-dob':
      return {
        type: 'field-group',
        fields: [{ type: 'date', id: 'dob', label: 'Date of Birth', required: true }],
      };
    case 'field-text':
      return {
        type: 'field-group',
        halfWidth: true,
        fields: [{ type: 'text', id: `field-${makeId()}`, label: 'Short Text' }],
      };
    case 'field-textarea':
      return {
        type: 'field-group',
        fields: [{ type: 'textarea', id: `field-${makeId()}`, label: 'Long Text' }],
      };
    case 'field-dropdown':
      return {
        type: 'field-group',
        halfWidth: true,
        fields: [
          {
            type: 'select',
            id: `field-${makeId()}`,
            label: 'Dropdown',
            options: [
              { value: '', label: '--- Select ---' },
              { value: 'option1', label: 'Option 1' },
              { value: 'option2', label: 'Option 2' },
            ],
          },
        ],
      };
    case 'print-name':
      return {
        type: 'print-name',
        id: `pn-${makeId()}`,
        nameLabel: 'Your Name (print):',
        namePlaceholder: 'Print your name',
        nameRequired: true,
        signingAsLabel: 'Signing as:',
        signingAsOptions: [
          { value: 'myself', label: 'Myself (the patient)' },
          { value: 'parent', label: 'Parent of patient' },
          { value: 'guardian', label: 'Legal Guardian' },
          { value: 'poa', label: 'Power of Attorney' },
          { value: 'other', label: 'Other' },
        ],
        signingAsRequired: true,
        acknowledgment:
          'By signing below, I acknowledge that I have read, understood, and agree to the information and terms contained in this document.',
      };
    case 'field-date':
      return {
        type: 'field-group',
        fields: [{ type: 'date', id: `field-${makeId()}`, label: 'Date' }],
      };
    case 'office-use':
      return {
        type: 'office-use',
        label: 'For Office Use',
        layoutType: 'table' as const,
        columns: [
          { id: `col-${makeId()}`, label: 'Column 1', width: 33 },
          { id: `col-${makeId()}`, label: 'Column 2', width: 33 },
          { id: `col-${makeId()}`, label: 'Column 3', width: 34 },
        ],
      };
    case 'field-email':
      return {
        type: 'field-group',
        halfWidth: true,
        fields: [{ type: 'text', id: `field-${makeId()}`, label: 'Email' }],
      };
    case 'field-phone':
      return {
        type: 'field-group',
        halfWidth: true,
        fields: [{ type: 'tel', id: `field-${makeId()}`, label: 'Phone Number' }],
      };
    case 'radio-group':
      return {
        type: 'checklist',
        id: makeId(),
        label: 'Radio',
        variant: 'radio' as const,
        options: [
          { id: makeId(), label: 'Option 1' },
          { id: makeId(), label: 'Option 2' },
        ],
      };
    case 'field-time':
      return {
        type: 'time-field' as const,
        id: makeId(),
        label: 'Time',
        required: false,
        use24hr: false,
      };
    case 'address':
      return {
        type: 'address' as const,
        id: makeId(),
        subfields: [
          {
            key: 'address1',
            label: 'Address 1',
            required: true,
            enabled: true,
            halfWidth: false,
            fieldType: 'text' as const,
          },
          {
            key: 'address2',
            label: 'Address 2',
            required: false,
            enabled: true,
            halfWidth: false,
            fieldType: 'text' as const,
          },
          { key: 'city', label: 'City', required: true, enabled: true, halfWidth: true, fieldType: 'text' as const },
          {
            key: 'state',
            label: 'State / Province / Region',
            required: true,
            enabled: true,
            halfWidth: true,
            fieldType: 'text' as const,
          },
          {
            key: 'zip',
            label: 'Postal / Zip Code',
            required: true,
            enabled: true,
            halfWidth: true,
            fieldType: 'text' as const,
          },
          {
            key: 'country',
            label: 'Country',
            required: false,
            enabled: true,
            halfWidth: true,
            fieldType: 'select-country' as const,
          },
        ],
      };
    case 'scale':
      return {
        type: 'scale' as const,
        id: makeId(),
        label: 'Enter a rating from 0 to 10.',
        scaleType: 'numeric' as const,
        min: 0,
        max: 10,
        step: 1,
        leftLabel: 'No Pain',
        rightLabel: 'Severe Pain',
        required: false,
        orientation: 'horizontal' as const,
        allowHalf: false,
        emojiCount: 5 as const,
        emojiType: 'emoji' as const,
        emojiLabels: ['Very Sad', 'Sad', 'Neutral', 'Happy', 'Very Happy'],
        likertOptions: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'],
      };
    case 'scale-pain':
      return {
        type: 'scale' as const,
        id: makeId(),
        label: 'How are you feeling today?',
        scaleType: 'emoji' as const,
        min: 1,
        max: 5,
        step: 1,
        leftLabel: '',
        rightLabel: '',
        required: false,
        orientation: 'horizontal' as const,
        allowHalf: false,
        emojiCount: 5 as const,
        emojiType: 'emoji' as const,
        emojiLabels: ['Very Sad', 'Sad', 'Neutral', 'Happy', 'Very Happy'],
        likertOptions: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'],
      };
    case 'scale-mood':
      return {
        type: 'scale' as const,
        id: makeId(),
        label: 'Rate your experience.',
        scaleType: 'stars' as const,
        min: 1,
        max: 5,
        step: 1,
        leftLabel: 'Poor',
        rightLabel: 'Excellent',
        required: false,
        orientation: 'horizontal' as const,
        allowHalf: false,
        emojiCount: 5 as const,
        emojiType: 'emoji' as const,
        emojiLabels: ['Very Sad', 'Sad', 'Neutral', 'Happy', 'Very Happy'],
        likertOptions: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'],
      };
    case 'scale-stars':
      return {
        type: 'scale' as const,
        id: makeId(),
        label: 'Rate on a scale.',
        scaleType: 'slider' as const,
        min: 0,
        max: 10,
        step: 1,
        leftLabel: 'Low',
        rightLabel: 'High',
        required: false,
        orientation: 'horizontal' as const,
        allowHalf: false,
        emojiCount: 5 as const,
        emojiType: 'emoji' as const,
        emojiLabels: ['Very Sad', 'Sad', 'Neutral', 'Happy', 'Very Happy'],
        likertOptions: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'],
      };
    case 'scale-likert':
      return {
        type: 'scale' as const,
        id: makeId(),
        label: 'Please indicate your level of agreement.',
        scaleType: 'likert' as const,
        min: 1,
        max: 5,
        step: 1,
        leftLabel: '',
        rightLabel: '',
        required: false,
        orientation: 'horizontal' as const,
        allowHalf: false,
        emojiCount: 5 as const,
        emojiType: 'emoji' as const,
        emojiLabels: ['Very Sad', 'Sad', 'Neutral', 'Happy', 'Very Happy'],
        likertOptions: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'],
      };
    case 'matrix':
      return {
        type: 'matrix' as const,
        id: makeId(),
        label: '',
        columns: ['Never', 'Sometimes', 'Often'],
        rows: [
          { id: makeId(), text: 'I feel anxious' },
          { id: makeId(), text: 'I sleep poorly' },
        ],
        required: false,
      };
    case 'image':
      return {
        type: 'image' as const,
        src: '',
        alt: '',
        alignment: 'center' as const,
        widthPercent: 100,
        halfWidth: false,
      };
    case 'video':
      return { type: 'video' as const, src: '', caption: '', aspectRatio: '16:9' as const, halfWidth: false };
    case 'header':
      return { type: 'header' as const, text: 'Header Text', fontSize: 30, color: '', marginTop: 8, marginBottom: 8 };
    case 'divider':
      return {
        type: 'divider' as const,
        thickness: 1,
        lengthPercent: 100,
        lineType: 'solid' as const,
        color: '#e2e8f0',
        marginTop: 16,
        marginBottom: 16,
      };
    default:
      return { type: 'paragraph', text: '' };
  }
}

export function useFormBuilderState(initial?: {
  settings: FormBuilderSettings;
  sections: BuilderSection[];
}): UseFormBuilderStateReturn {
  const [settings, setSettings] = useState<FormBuilderSettings>(
    initial?.settings ?? {
      title: '',
      subtitle: '',
      intro:
        'This consent is for all telehealth services provided to me by {providerName} through the HIPAA LINK secure telehealth platform.',
      pdfTitle: '',
      includeOfficeUseTable: true,
      isEnabled: false,
      headerSubtitle: '',
    },
  );

  const [sections, setSections] = useState<BuilderSection[]>(initial?.sections ?? []);
  const [history, setHistory] = useState<BuilderSection[][]>([]);
  const [future, setFuture] = useState<BuilderSection[][]>([]);

  // Keep a ref to current sections so push helper can read without stale closure
  const sectionsRef = useRef<BuilderSection[]>(sections);
  sectionsRef.current = sections;

  const pushToHistory = useCallback(() => {
    const snapshot = sectionsRef.current;
    setHistory((prev) => {
      const next = [...prev, snapshot];
      return next.length > MAX_HISTORY ? next.slice(next.length - MAX_HISTORY) : next;
    });
    setFuture([]);
  }, []);

  const undo = useCallback(() => {
    setHistory((prev) => {
      if (prev.length === 0) return prev;
      const restored = prev[prev.length - 1];
      const newHistory = prev.slice(0, -1);
      setFuture((f) => {
        const cap = [...f];
        cap.unshift(sectionsRef.current);
        return cap.length > MAX_HISTORY ? cap.slice(0, MAX_HISTORY) : cap;
      });
      setSections(restored);
      return newHistory;
    });
  }, []);

  const redo = useCallback(() => {
    setFuture((prev) => {
      if (prev.length === 0) return prev;
      const [next, ...rest] = prev;
      setHistory((h) => {
        const cap = [...h, sectionsRef.current];
        return cap.length > MAX_HISTORY ? cap.slice(cap.length - MAX_HISTORY) : cap;
      });
      setSections(next);
      return rest;
    });
  }, []);

  const updateSettings = useCallback((patch: Partial<FormBuilderSettings>) => {
    setSettings((prev) => ({ ...prev, ...patch }));
  }, []);

  const hydrateSections = useCallback((rawSections: ConsentSection[]) => {
    setSections(
      rawSections.map((s) => ({
        _id: s.id || makeId(),
        navLabel: s.navLabel || 'Section',
        navLabelOverride: s.navLabel || '',
        heading: s.heading || '',
        sectionTitle: s.sectionTitle || '',
        hideSectionTitle: s.hideSectionTitle ?? false,
        blocks: s.blocks.filter((b) => !isOfficeUseBlock(b)).map((b) => ({ ...b, _id: makeId() } as BuilderBlock)),
      })),
    );
    setHistory([]);
    setFuture([]);
  }, []);

  const addSection = useCallback(() => {
    pushToHistory();
    setSections((prev) => {
      const n = prev.length + 1;
      return [
        ...prev,
        {
          _id: makeId(),
          navLabel: `Section ${n}`,
          navLabelOverride: '',
          heading: `Section ${n}`,
          sectionTitle: '',
          hideSectionTitle: false,
          blocks: [],
        },
      ];
    });
  }, [pushToHistory]);

  const removeSection = useCallback(
    (sectionId: string) => {
      pushToHistory();
      setSections((prev) => prev.filter((s) => s._id !== sectionId));
    },
    [pushToHistory],
  );

  const updateSection = useCallback(
    (
      sectionId: string,
      patch: Partial<Pick<BuilderSection, 'navLabelOverride' | 'heading' | 'sectionTitle' | 'hideSectionTitle'>>,
    ) => {
      pushToHistory();
      setSections((prev) => prev.map((s) => (s._id === sectionId ? { ...s, ...patch } : s)));
    },
    [pushToHistory],
  );

  const reorderSections = useCallback(
    (fromIndex: number, toIndex: number) => {
      pushToHistory();
      setSections((prev) => {
        const next = [...prev];
        const [moved] = next.splice(fromIndex, 1);
        next.splice(toIndex, 0, moved);
        return next;
      });
    },
    [pushToHistory],
  );

  const addBlock = useCallback(
    (sectionId: string, blockType: string, atIndex?: number) => {
      pushToHistory();
      const newBlock: BuilderBlock = { ...makeDefaultBlock(blockType), _id: makeId() } as BuilderBlock;
      setSections((prev) =>
        prev.map((s) => {
          if (s._id !== sectionId) return s;
          const blocks = [...s.blocks];
          if (atIndex !== undefined) {
            blocks.splice(atIndex, 0, newBlock);
          } else {
            blocks.push(newBlock);
          }
          return { ...s, blocks };
        }),
      );
    },
    [pushToHistory],
  );

  const removeBlock = useCallback(
    (sectionId: string, blockId: string) => {
      pushToHistory();
      setSections((prev) =>
        prev.map((s) => {
          if (s._id !== sectionId) return s;
          return { ...s, blocks: s.blocks.filter((b) => b._id !== blockId) };
        }),
      );
    },
    [pushToHistory],
  );

  const updateBlock = useCallback(
    (sectionId: string, blockId: string, patch: Partial<ContentBlock>) => {
      pushToHistory();
      setSections((prev) =>
        prev.map((s) => {
          if (s._id !== sectionId) return s;
          return {
            ...s,
            blocks: s.blocks.map((b) => (b._id === blockId ? ({ ...b, ...patch } as BuilderBlock) : b)),
          };
        }),
      );
    },
    [pushToHistory],
  );

  const reorderBlocks = useCallback(
    (sectionId: string, fromIndex: number, toIndex: number) => {
      pushToHistory();
      setSections((prev) =>
        prev.map((s) => {
          if (s._id !== sectionId) return s;
          const blocks = [...s.blocks];
          const [moved] = blocks.splice(fromIndex, 1);
          blocks.splice(toIndex, 0, moved);
          return { ...s, blocks };
        }),
      );
    },
    [pushToHistory],
  );

  const moveBlock = useCallback(
    (fromSectionId: string, blockId: string, toSectionId: string, toIndex: number) => {
      pushToHistory();
      setSections((prev) => {
        const fromSection = prev.find((s) => s._id === fromSectionId);
        const block = fromSection?.blocks.find((b) => b._id === blockId);
        if (!block) return prev;
        return prev.map((s) => {
          if (s._id === fromSectionId) {
            return { ...s, blocks: s.blocks.filter((b) => b._id !== blockId) };
          }
          if (s._id === toSectionId) {
            const blocks = [...s.blocks];
            blocks.splice(toIndex, 0, block);
            return { ...s, blocks };
          }
          return s;
        });
      });
    },
    [pushToHistory],
  );

  const duplicateBlock = useCallback(
    (sectionId: string, blockId: string) => {
      pushToHistory();
      setSections((prev) =>
        prev.map((s) => {
          if (s._id !== sectionId) return s;
          const idx = s.blocks.findIndex((b) => b._id === blockId);
          if (idx < 0) return s;
          const original = s.blocks[idx];
          const cloned = JSON.parse(JSON.stringify(original)) as BuilderBlock;
          cloned._id = makeId();
          // Rename label(s) so the copy is distinguishable
          if ('fields' in cloned && Array.isArray(cloned.fields)) {
            cloned.fields = (cloned.fields as unknown[]).map((f) => {
              const field = f as Record<string, unknown>;
              return {
                ...field,
                id: typeof field.id === 'string' ? `${field.id}-${makeId()}` : field.id,
                label: typeof field.label === 'string' ? `${field.label} (copy)` : field.label,
              };
            }) as unknown as typeof cloned.fields;
          } else if ('label' in cloned && typeof cloned.label === 'string') {
            (cloned as Record<string, unknown>).label = `${cloned.label} (copy)`;
          }
          const blocks = [...s.blocks];
          blocks.splice(idx + 1, 0, cloned);
          return { ...s, blocks };
        }),
      );
    },
    [pushToHistory],
  );

  const serialize = useCallback((): UpdateCustomConsentFormPayload => {
    const consentSections = sections.map((s, i) => toConsentSection(s, i));

    if (settings.includeOfficeUseTable && consentSections.length > 0) {
      const last = consentSections[consentSections.length - 1];
      last.blocks = [...last.blocks, OFFICE_USE_BLOCK];
    }

    const requiredFields: string[] = [];
    for (const section of consentSections) {
      for (const block of section.blocks) {
        if (block.type === 'field-group') {
          for (const field of block.fields) {
            if ('required' in field && field.required && 'id' in field) {
              requiredFields.push(field.id);
            }
          }
        }
      }
    }

    return {
      title: settings.title,
      subtitle: settings.subtitle || undefined,
      intro: settings.intro || undefined,
      pdfTitle: settings.pdfTitle || settings.title,
      includeOfficeUseTable: settings.includeOfficeUseTable,
      isEnabled: settings.isEnabled,
      definition: JSON.stringify({
        sections: consentSections,
        requiredFields,
        headerSubtitle: settings.headerSubtitle || undefined,
      }),
    };
  }, [settings, sections]);

  return {
    settings,
    sections,
    canUndo: history.length > 0,
    canRedo: future.length > 0,
    undo,
    redo,
    updateSettings,
    hydrateSections,
    addSection,
    removeSection,
    updateSection,
    reorderSections,
    addBlock,
    duplicateBlock,
    removeBlock,
    updateBlock,
    reorderBlocks,
    moveBlock,
    serialize,
  };
}
